/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, Fragment, useState, useEffect } from "react"
import { Divider, Flex, TimePicker } from "@duxton-web/ui"
import { SizeType } from "@duxton-web/ui/es/config-provider/SizeContext"
import { range } from "lodash"
import dayjs, { Dayjs } from "dayjs"

export interface FormOptions {
  form: WrappedFormUtils
  startKey: string
  endKey: string
}

export interface TimeRangePickerProps {
  format: string
  width?: number
  start?: string
  end?: string
  size: SizeType
  formOptions?: FormOptions
  onChangeStartTime?(time: Dayjs, timeString: string): void
  onChangeEndTime?(time: Dayjs, timeString: string): void
}

// defaults
const FORMAT = "HH:mm"
const START = "00:00"
const END = "24:00"

export const TimeRangePicker: FC<TimeRangePickerProps> = ({
  formOptions,
  format,
  width,
  size,
  start,
  end,
  onChangeStartTime,
  onChangeEndTime,
}: TimeRangePickerProps) => {
  const [startState, setStartState] = useState(
    start ? dayjs(start, format) : dayjs(START, FORMAT),
  )
  const [endState, setEndState] = useState(
    end ? dayjs(end, format) : dayjs(END, FORMAT),
  )

  const timePickerStyle = css`
    width: ${width}px;
    * {
      text-align: center;
    }
  `

  const handleChangeStart = (time: Dayjs | null, timeString: string) => {
    const newTime = time ?? dayjs(START, FORMAT)
    const newTimeString = time ? timeString : newTime.format(format)
    setStartState(newTime)
    onChangeStartTime?.(newTime, newTimeString)
    formOptions?.form?.setFieldsValue({
      [formOptions.startKey]: newTimeString,
    })
  }

  const handleChangeEnd = (time: Dayjs, timeString: string) => {
    const newTime = time ?? dayjs(END, FORMAT)
    const newTimeString = time ? timeString : newTime.format(format)
    setEndState(newTime)
    onChangeEndTime?.(newTime, newTimeString)
    formOptions?.form?.setFieldsValue({
      [formOptions.endKey]: newTimeString,
    })
  }

  const formStart = formOptions?.form.getFieldValue(formOptions.startKey)
  useEffect(() => {
    if (!formOptions?.form) return
    setStartState(dayjs(formStart, format))
  }, [formOptions, formStart, format])
  const formEnd = formOptions?.form.getFieldValue(formOptions.endKey)
  useEffect(() => {
    if (!formOptions?.form) return
    setEndState(dayjs(formEnd, format))
  }, [formEnd, formOptions, format])

  return (
    <Flex align="center">
      {formOptions && (
        <Fragment>
          {formOptions.form.getFieldDecorator(formOptions.startKey, {
            initialValue: start,
          })(<input type="hidden" />)}
          {formOptions.form.getFieldDecorator(formOptions.endKey, {
            initialValue: end,
          })(<input type="hidden" />)}
        </Fragment>
      )}
      <TimePicker
        format={format}
        size={size}
        css={timePickerStyle}
        value={startState}
        onChange={(time, timeString) => {
          handleChangeStart(time, timeString as string)
          if (time?.isAfter(endState)) {
            const newEndTime = dayjs(START, FORMAT)
            const newEndTimeString = newEndTime.format(format)
            handleChangeEnd(newEndTime, newEndTimeString)
          }
        }}
        allowClear={false}
        suffixIcon={null}
      />
      <Divider
        css={css`
          width: 8px;
          margin: 0 8px;
          min-width: unset;
        `}
      />
      <TimePicker
        format={format}
        size={size}
        css={timePickerStyle}
        value={endState}
        disabledTime={() => ({
          disabledHours: () =>
            range(
              startState.minute() !== 59
                ? startState.hour()
                : startState.hour() + 1,
            ),
          disabledMinutes: selectedHour =>
            selectedHour === startState.hour()
              ? range(startState.minute() + 1)
              : [],
        })}
        onChange={handleChangeEnd}
        hideDisabledOptions
        allowClear={false}
        suffixIcon={null}
      />
    </Flex>
  )
}
